import { call, put, takeLatest } from 'redux-saga/effects';
import { UPDATE_ACCOUNT } from 'store/settings';
import { settingsService } from 'store/settings/service';
import { authService } from './service';
import { removeStorage, setStorage } from 'utils/storageHelpers';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';
import {
  LOG_IN,
  LOG_IN_SUCCESS,
  LOG_OUT,
  RESET_PASSWORD_REQUEST,
  CREATE_USER,
  GET_USER_INFO,
  GET_USER_INFO_SUCCESS,
  SUBSCRIBE,
  NEW_PASSWORD,
  SOCIAL_LOGIN,
  EMAIL_VERIFY,
  SOCIAL_LOGIN_EMAIL_VERIFICATION,
  PASSWORD_RECOVERY_EMAIL_VERIFY,
} from './types';

function* logIn({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));
  try {
    const res = yield call(authService.signIn, payload);
    setStorage('credentials', res.data);

    yield put({
      type: LOG_IN_SUCCESS,
      payload: res.data,
    });
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    yield put(httpRequestsOnErrorsActions.removeError(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err.data));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* socialLogin({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));
  try {
    const res = yield call(authService.socialLogin, payload);
    setStorage('credentials', res.data);
    if (res.data) {
      removeStorage('fbAccessToken');
    }

    yield put({
      type: LOG_IN_SUCCESS,
      payload: res.data,
    });

    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err.data.message));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* logOut() {
  try {
    removeStorage('credentials');
    window.location.replace('/welcome');
  } catch (err) {
    if (err.response.data.statusCode === 401) {
      removeStorage('access-token');
      window.location.replace('/login');
    }
  }
}

function* resetPasswordSaga({ type, payload }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));

  try {
    yield call(authService.getForgotPasswordLink, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err.data.message));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* createUser({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));
  try {
    yield call(authService.userReg, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* userInfoSaga({ type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.removeLoading(type));
  try {
    const res = yield call(authService.userinfoService);
    yield put({ type: GET_USER_INFO_SUCCESS, payload: res.data });
  } catch (err) {
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    removeStorage('credentials');
    yield put(httpRequestsOnErrorsActions.appendError(type, err.data));
  }
}

function* updateAccount({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.removeLoading(type));
  try {
    yield call(settingsService.updateAccount, payload);
    const res = yield call(authService.userinfoService);
    yield put({ type: GET_USER_INFO_SUCCESS, payload: res.data });
  } catch (err) {
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* subscribe({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnSuccessActions.removeSuccess(type));

  try {
    yield call(authService.subscribe, payload);
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
  }
}

function* createNewPassword({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));

  try {
    yield call(authService.newPassword, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* emailVerify({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));

  try {
    yield call(authService.emailVerify, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    removeStorage('email');
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* socialLoginEmailVerification({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));

  try {
    yield call(authService.socilaLoginEmailVerify, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    setStorage('email', payload?.email);
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

function* sendCodeRecoveryPassword({ payload, type }) {
  yield put(httpRequestsOnErrorsActions.removeError(type));
  yield put(httpRequestsOnLoadActions.appendLoading(type));

  try {
    yield call(authService.sendCodeForgotPassword, payload);
    yield put(httpRequestsOnLoadActions.removeLoading(type));
    yield put(httpRequestsOnSuccessActions.appendSuccess(type));
  } catch (err) {
    yield put(httpRequestsOnErrorsActions.appendError(type, err));
    yield put(httpRequestsOnLoadActions.removeLoading(type));
  }
}

export const watchAuth = function* watchUserAuth() {
  yield takeLatest(CREATE_USER, createUser);
  yield takeLatest(LOG_IN, logIn);
  yield takeLatest(LOG_OUT, logOut);
  yield takeLatest(RESET_PASSWORD_REQUEST, resetPasswordSaga);
  yield takeLatest(GET_USER_INFO, userInfoSaga);
  yield takeLatest(UPDATE_ACCOUNT, updateAccount);
  yield takeLatest(SUBSCRIBE, subscribe);
  yield takeLatest(NEW_PASSWORD, createNewPassword);
  yield takeLatest(SOCIAL_LOGIN, socialLogin);
  yield takeLatest(EMAIL_VERIFY, emailVerify);
  yield takeLatest(SOCIAL_LOGIN_EMAIL_VERIFICATION, socialLoginEmailVerification);
  yield takeLatest(PASSWORD_RECOVERY_EMAIL_VERIFY, sendCodeRecoveryPassword);
};
